import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Menu,
  MenuItem,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  PersonAdd as PersonAddIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Message as MessageIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { format } from 'date-fns';

// Types
interface Trainer {
  id: string;
  name: string;
  email: string;
  status: 'pending' | 'approved' | 'revoked';
  connectedAt: string;
  lastActivity?: string;
  notes?: string;
}

interface RequestTrainerFormData {
  email: string;
  message: string;
}

// Validation schema
const requestTrainerSchema = yup.object({
  email: yup
    .string()
    .email('Invalid email address')
    .required('Email is required'),
  message: yup
    .string()
    .max(500, 'Message must be at most 500 characters')
    .optional(),
});

const MyTrainersPage: React.FC = () => {
  // State management
  const [trainers, setTrainers] = useState<Trainer[]>([
    {
      id: '1',
      name: 'John Smith',
      email: 'john.smith@example.com',
      status: 'approved',
      connectedAt: '2024-01-15T10:00:00Z',
      lastActivity: '2024-12-18T14:30:00Z',
      notes: 'Primary coach for running workouts',
    },
    {
      id: '2',
      name: 'Sarah Johnson',
      email: 'sarah.j@example.com',
      status: 'pending',
      connectedAt: '2024-12-10T09:00:00Z',
      notes: 'Cycling specialist',
    },
  ]);
  const [isRequestDialogOpen, setIsRequestDialogOpen] = useState(false);
  const [selectedTrainer, setSelectedTrainer] = useState<Trainer | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [queuedActions, setQueuedActions] = useState<number>(0);

  // Form handling
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<RequestTrainerFormData>({
    resolver: yupResolver(requestTrainerSchema),
    defaultValues: {
      email: '',
      message: '',
    },
  });

  // Handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, trainer: Trainer) => {
    setAnchorEl(event.currentTarget);
    setSelectedTrainer(trainer);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTrainer(null);
  };

  const handleApproveConsent = async (trainerId: string) => {
    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      
      setTrainers((prev) =>
        prev.map((trainer) =>
          trainer.id === trainerId
            ? { ...trainer, status: 'approved' as const }
            : trainer
        )
      );

      if (isOffline) {
        setQueuedActions((prev) => prev + 1);
      }
    } catch (error) {
      console.error('Failed to approve consent:', error);
    } finally {
      setIsLoading(false);
      handleMenuClose();
    }
  };

  const handleRevokeConsent = async (trainerId: string) => {
    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      
      setTrainers((prev) =>
        prev.map((trainer) =>
          trainer.id === trainerId
            ? { ...trainer, status: 'revoked' as const }
            : trainer
        )
      );

      if (isOffline) {
        setQueuedActions((prev) => prev + 1);
      }
    } catch (error) {
      console.error('Failed to revoke consent:', error);
    } finally {
      setIsLoading(false);
      handleMenuClose();
    }
  };

  const handleRemoveTrainer = async (trainerId: string) => {
    if (!window.confirm('Are you sure you want to remove this trainer connection?')) {
      return;
    }

    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      
      setTrainers((prev) => prev.filter((trainer) => trainer.id !== trainerId));

      if (isOffline) {
        setQueuedActions((prev) => prev + 1);
      }
    } catch (error) {
      console.error('Failed to remove trainer:', error);
    } finally {
      setIsLoading(false);
      handleMenuClose();
    }
  };

  const handleRequestTrainer = async (data: RequestTrainerFormData) => {
    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      
      const newTrainer: Trainer = {
        id: Date.now().toString(),
        name: 'Pending',
        email: data.email,
        status: 'pending',
        connectedAt: new Date().toISOString(),
        notes: data.message,
      };

      setTrainers((prev) => [...prev, newTrainer]);

      if (isOffline) {
        setQueuedActions((prev) => prev + 1);
      }

      setIsRequestDialogOpen(false);
      reset();
    } catch (error) {
      console.error('Failed to request trainer:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: Trainer['status']) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'revoked':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: Trainer['status']) => {
    switch (status) {
      case 'approved':
        return 'Approved';
      case 'pending':
        return 'Pending';
      case 'revoked':
        return 'Revoked';
      default:
        return 'Unknown';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Offline Banner */}
      {isOffline && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You are currently offline. Changes will be synced when connection is restored.
          {queuedActions > 0 && ` (${queuedActions} action${queuedActions > 1 ? 's' : ''} queued)`}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          My Trainers
        </Typography>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setIsRequestDialogOpen(true)}
          disabled={isLoading}
        >
          Request New Trainer
        </Button>
      </Box>

      {/* Trainers List */}
      {trainers.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No trainers connected
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Request a trainer connection to get personalized workout plans and coaching.
          </Typography>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setIsRequestDialogOpen(true)}
          >
            Request New Trainer
          </Button>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {trainers.map((trainer) => (
            <Card key={trainer.id} elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" component="h2" gutterBottom>
                      {trainer.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {trainer.email}
                    </Typography>
                    <Chip
                      label={getStatusLabel(trainer.status)}
                      color={getStatusColor(trainer.status)}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                  <IconButton
                    onClick={(e) => handleMenuOpen(e, trainer)}
                    disabled={isLoading}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Connected:</strong> {format(new Date(trainer.connectedAt), 'MMM dd, yyyy')}
                  </Typography>
                  {trainer.lastActivity && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Last Activity:</strong> {format(new Date(trainer.lastActivity), 'MMM dd, yyyy HH:mm')}
                    </Typography>
                  )}
                  {trainer.notes && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Notes:</strong> {trainer.notes}
                    </Typography>
                  )}
                </Box>
              </CardContent>

              <CardActions sx={{ px: 2, pb: 2 }}>
                {trainer.status === 'pending' && (
                  <Button
                    size="small"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => handleApproveConsent(trainer.id)}
                    disabled={isLoading}
                  >
                    Approve
                  </Button>
                )}
                {trainer.status === 'approved' && (
                  <Button
                    size="small"
                    startIcon={<CancelIcon />}
                    onClick={() => handleRevokeConsent(trainer.id)}
                    disabled={isLoading}
                  >
                    Revoke Access
                  </Button>
                )}
                <Button
                  size="small"
                  startIcon={<MessageIcon />}
                  disabled={isLoading}
                >
                  Message
                </Button>
              </CardActions>
            </Card>
          ))}
        </Box>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedTrainer?.status === 'pending' && (
          <MenuItem onClick={() => selectedTrainer && handleApproveConsent(selectedTrainer.id)}>
            <CheckCircleIcon sx={{ mr: 1 }} fontSize="small" />
            Approve Consent
          </MenuItem>
        )}
        {selectedTrainer?.status === 'approved' && (
          <MenuItem onClick={() => selectedTrainer && handleRevokeConsent(selectedTrainer.id)}>
            <CancelIcon sx={{ mr: 1 }} fontSize="small" />
            Revoke Consent
          </MenuItem>
        )}
        <MenuItem onClick={() => selectedTrainer && handleRemoveTrainer(selectedTrainer.id)}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" color="error" />
          <Typography color="error">Remove Trainer</Typography>
        </MenuItem>
      </Menu>

      {/* Request Trainer Dialog */}
      <Dialog
        open={isRequestDialogOpen}
        onClose={() => !isLoading && setIsRequestDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleSubmit(handleRequestTrainer)}>
          <DialogTitle>Request New Trainer</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Trainer Email"
                    type="email"
                    fullWidth
                    required
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    disabled={isLoading}
                  />
                )}
              />
              <Controller
                name="message"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Message (Optional)"
                    multiline
                    rows={4}
                    fullWidth
                    error={!!errors.message}
                    helperText={errors.message?.message || 'Introduce yourself to your trainer'}
                    disabled={isLoading}
                  />
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setIsRequestDialogOpen(false);
                reset();
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} /> : <PersonAddIcon />}
            >
              Send Request
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};

export default MyTrainersPage;